import React, { useState, useRef, useEffect } from 'react';
import { 
  Gift, CreditCard, Send, ChevronLeft, Upload, Image, Check, 
  Mail, User, DollarSign, MessageSquare, Loader2, X, Camera, CheckCircle, PartyPopper,
  Banknote, Building, ShoppingCart, Plus, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { giftCardsAPI } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Default gift card denominations
const GIFT_CARD_AMOUNTS = [25, 50, 75, 100, 150, 200, 250, 500];

// Payment methods for gift card purchase
const PAYMENT_METHODS = [
  { id: 'card', name: 'Credit/Debit Card', icon: CreditCard, description: 'Secure payment via Stripe' },
  { id: 'etransfer', name: 'E-Transfer / Bank Transfer', icon: Building, description: 'Pay via Interac e-Transfer' },
  { id: 'cash', name: 'Cash (In-Store)', icon: Banknote, description: 'Pay at our store location' },
];

// Default gift card images (admin can add more)
const DEFAULT_GIFT_CARD_IMAGES = [
  {
    id: 'store',
    url: 'https://base44.app/api/apps/6939fbb5c2c7684a1f6e4ebc/files/public/6939fbb5c2c7684a1f6e4ebc/0d15e8e76_IMG_0948.jpeg',
    name: 'Store Front'
  },
  {
    id: 'african',
    url: 'https://images.unsplash.com/photo-1604329760661-e71dc83f8f26?w=800',
    name: 'African Cuisine'
  },
  {
    id: 'spices',
    url: 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=800',
    name: 'Spices & Flavors'
  },
  {
    id: 'celebration',
    url: 'https://images.unsplash.com/photo-1513151233558-d860c5398176?w=800',
    name: 'Celebration'
  },
];

const GiftCards = ({ onBack, user, cart, setCart, onCheckout }) => {
  // Form state
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [senderName, setSenderName] = useState(user?.name || '');
  const [message, setMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState(DEFAULT_GIFT_CARD_IMAGES[0]);
  const [customImage, setCustomImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState(1); // 1: Design, 2: Details, 3: Payment, 4: Success
  const [purchaseSuccess, setPurchaseSuccess] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');
  const [etransferConfirmation, setEtransferConfirmation] = useState('');
  const [etransferScreenshot, setEtransferScreenshot] = useState(null);
  
  const fileInputRef = useRef(null);
  const etransferScreenshotRef = useRef(null);
  const pollIntervalRef = useRef(null);
  
  const finalAmount = customAmount ? parseFloat(customAmount) : selectedAmount;
  const isValidAmount = finalAmount >= 10 && finalAmount <= 1000;

  // Check for return from Stripe checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const success = urlParams.get('success');
    const cancelled = urlParams.get('cancelled');

    if (cancelled) {
      toast.error('Payment was cancelled');
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    if (success && sessionId) {
      setStep(4);
      setIsProcessing(true);
      
      const checkPaymentStatus = async () => {
        try {
          const status = await giftCardsAPI.checkPaymentStatus(sessionId);
          
          if (status.status === 'completed') {
            setPurchaseSuccess({
              code: status.gift_card_code,
              activated: status.gift_card_activated
            });
            setIsProcessing(false);
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
            }
            window.history.replaceState({}, '', window.location.pathname);
          } else if (status.status === 'failed') {
            toast.error('Payment failed. Please try again.');
            setStep(3);
            setIsProcessing(false);
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
            }
            window.history.replaceState({}, '', window.location.pathname);
          }
        } catch (error) {
          console.error('Error checking payment status:', error);
        }
      };

      checkPaymentStatus();
      pollIntervalRef.current = setInterval(checkPaymentStatus, 2000);

      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setCustomImage(event.target?.result);
        setSelectedImage(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEtransferScreenshot = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Screenshot must be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setEtransferScreenshot(event.target?.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const getGiftCardImage = () => {
    if (customImage) return customImage;
    return selectedImage?.url || DEFAULT_GIFT_CARD_IMAGES[0].url;
  };

  const canProceedToStep2 = () => {
    return isValidAmount && (selectedImage || customImage);
  };

  const canProceedToStep3 = () => {
    return recipientEmail && recipientName && senderName && 
           /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail);
  };

  const canCompletePurchase = () => {
    if (selectedPaymentMethod === 'card') return true;
    if (selectedPaymentMethod === 'etransfer') {
      return etransferConfirmation.length >= 5;
    }
    if (selectedPaymentMethod === 'cash') return true;
    return false;
  };

  // Add gift card to cart for combined purchase
  const addToCart = () => {
    if (!isValidAmount) {
      toast.error('Please select a valid amount');
      return;
    }
    if (!recipientEmail || !recipientName || !senderName) {
      toast.error('Please fill in recipient details first');
      setStep(2);
      return;
    }

    const giftCardItem = {
      id: `giftcard-${Date.now()}`,
      name: `Gift Card for ${recipientName}`,
      price: finalAmount,
      quantity: 1,
      isGiftCard: true,
      giftCardDetails: {
        amount: finalAmount,
        recipientEmail,
        recipientName,
        senderName,
        message,
        image: getGiftCardImage()
      }
    };

    if (setCart) {
      setCart(prevCart => [...prevCart, giftCardItem]);
      toast.success(`$${finalAmount} Gift Card added to cart!`);
      if (onBack) onBack();
    } else {
      toast.error('Cannot add to cart at this time');
    }
  };

  const handlePurchase = async () => {
    if (!canCompletePurchase()) {
      toast.error('Please complete all required fields');
      return;
    }

    setIsProcessing(true);

    try {
      if (selectedPaymentMethod === 'card') {
        // Stripe checkout
        const response = await giftCardsAPI.createCheckout({
          amount: finalAmount,
          recipient_email: recipientEmail,
          recipient_name: recipientName,
          sender_name: senderName,
          message: message || '',
          image_url: getGiftCardImage(),
          origin_url: window.location.origin
        });

        if (response.checkout_url) {
          window.location.href = response.checkout_url;
        } else {
          throw new Error('No checkout URL received');
        }
      } else if (selectedPaymentMethod === 'etransfer') {
        // E-Transfer purchase
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_URL}/api/gift-cards/purchase-etransfer`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify({
            amount: finalAmount,
            recipient_email: recipientEmail,
            recipient_name: recipientName,
            sender_name: senderName,
            message: message || '',
            image_url: getGiftCardImage(),
            etransfer_confirmation: etransferConfirmation,
            etransfer_screenshot: etransferScreenshot
          })
        });

        if (response.ok) {
          const data = await response.json();
          setPurchaseSuccess({
            code: data.gift_card_code,
            pending: true,
            message: 'Your e-transfer payment is pending verification. Gift card will be activated once verified.'
          });
          setStep(4);
        } else {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to process e-transfer purchase');
        }
      } else if (selectedPaymentMethod === 'cash') {
        // Cash purchase - creates pending order for in-store payment
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_URL}/api/gift-cards/purchase-cash`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify({
            amount: finalAmount,
            recipient_email: recipientEmail,
            recipient_name: recipientName,
            sender_name: senderName,
            message: message || '',
            image_url: getGiftCardImage()
          })
        });

        if (response.ok) {
          const data = await response.json();
          setPurchaseSuccess({
            code: data.reference_code,
            pending: true,
            isCash: true,
            message: 'Please visit our store to complete your purchase. Show this reference code at checkout.'
          });
          setStep(4);
        } else {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to create cash purchase order');
        }
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.message || 'Failed to process payment');
    } finally {
      setIsProcessing(false);
    }
  };

  const resetForm = () => {
    setSelectedAmount(50);
    setCustomAmount('');
    setRecipientEmail('');
    setRecipientName('');
    setSenderName(user?.name || '');
    setMessage('');
    setSelectedImage(DEFAULT_GIFT_CARD_IMAGES[0]);
    setCustomImage(null);
    setStep(1);
    setPurchaseSuccess(null);
    setSelectedPaymentMethod('card');
    setEtransferConfirmation('');
    setEtransferScreenshot(null);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" onClick={onBack} className="p-2">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Gift className="w-8 h-8 text-purple-500" />
            Gift Cards
          </h1>
          <p className="text-gray-500">Send the perfect gift to friends and family</p>
        </div>
      </div>

      {/* Progress Steps */}
      {step < 4 && (
        <div className="flex items-center justify-center gap-4 mb-8">
          {['Design', 'Details', 'Payment'].map((label, idx) => (
            <React.Fragment key={label}>
              <div className="flex flex-col items-center">
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    step > idx + 1 ? 'bg-green-500 text-white' :
                    step === idx + 1 ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {step > idx + 1 ? <Check className="w-5 h-5" /> : idx + 1}
                </div>
                <span className={`text-xs mt-1 ${step >= idx + 1 ? 'text-purple-600 font-medium' : 'text-gray-500'}`}>
                  {label}
                </span>
              </div>
              {idx < 2 && (
                <div className={`w-16 h-1 ${step > idx + 1 ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Preview Card */}
        <div className="lg:sticky lg:top-8">
          <Card className="overflow-hidden">
            <div className="relative aspect-[1.6/1]">
              <img 
                src={getGiftCardImage()} 
                alt="Gift Card" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <p className="text-sm opacity-80">UPLANDS FOOD MARKET</p>
                <p className="text-3xl font-bold">${finalAmount.toFixed(2)}</p>
                {recipientName && (
                  <p className="text-sm mt-2">For: {recipientName}</p>
                )}
              </div>
              <div className="absolute top-4 right-4">
                <Badge className="bg-purple-500 text-white">Digital Gift Card</Badge>
              </div>
            </div>
            {message && (
              <div className="p-4 bg-purple-50 border-t">
                <p className="text-sm text-gray-600 italic">&quot;{message}&quot;</p>
                <p className="text-xs text-gray-500 mt-1">- {senderName || 'Sender'}</p>
              </div>
            )}
          </Card>
        </div>

        {/* Form Steps */}
        <div>
          {/* Step 1: Design */}
          {step === 1 && (
            <Card className="p-6">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-purple-500" />
                Select Amount
              </h3>

              <div className="grid grid-cols-4 gap-2 mb-4">
                {GIFT_CARD_AMOUNTS.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => {
                      setSelectedAmount(amount);
                      setCustomAmount('');
                    }}
                    className={`p-3 rounded-lg border-2 font-semibold transition-all ${
                      selectedAmount === amount && !customAmount
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    ${amount}
                  </button>
                ))}
              </div>

              <div className="mb-6">
                <Label htmlFor="custom-amount">Or enter custom amount ($10 - $1,000)</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="custom-amount"
                    type="number"
                    min="10"
                    max="1000"
                    placeholder="Custom amount"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Image className="w-5 h-5 text-purple-500" />
                Choose Design
              </h3>

              <div className="grid grid-cols-2 gap-3 mb-4">
                {DEFAULT_GIFT_CARD_IMAGES.map((img) => (
                  <button
                    key={img.id}
                    onClick={() => {
                      setSelectedImage(img);
                      setCustomImage(null);
                    }}
                    className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage?.id === img.id && !customImage
                        ? 'border-purple-500 ring-2 ring-purple-200'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img src={img.url} alt={img.name} className="w-full h-full object-cover" />
                    {selectedImage?.id === img.id && !customImage && (
                      <div className="absolute inset-0 bg-purple-500/20 flex items-center justify-center">
                        <Check className="w-8 h-8 text-white drop-shadow-lg" />
                      </div>
                    )}
                  </button>
                ))}
              </div>

              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="w-full mb-6"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Custom Image
              </Button>

              <Button 
                onClick={() => setStep(2)}
                disabled={!canProceedToStep2()}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                Continue to Details
              </Button>
            </Card>
          )}

          {/* Step 2: Recipient Details */}
          {step === 2 && (
            <Card className="p-6">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-purple-500" />
                Recipient Details
              </h3>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="recipient-name">Recipient Name *</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="recipient-name"
                      placeholder="Who is this gift for?"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="recipient-email">Recipient Email *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="recipient-email"
                      type="email"
                      placeholder="recipient@email.com"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Gift card will be sent to this email address
                  </p>
                </div>

                <div>
                  <Label htmlFor="sender-name">Your Name *</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="sender-name"
                      placeholder="Your name"
                      value={senderName}
                      onChange={(e) => setSenderName(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message">Personal Message (Optional)</Label>
                  <div className="relative mt-1">
                    <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                    <textarea
                      id="message"
                      placeholder="Add a personal message..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg resize-none h-24 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      maxLength={200}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{message.length}/200 characters</p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                  Back
                </Button>
                <Button 
                  onClick={() => setStep(3)}
                  disabled={!canProceedToStep3()}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  Continue to Payment
                </Button>
              </div>

              {/* Add to Cart Option */}
              {setCart && (
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm text-gray-600 mb-3 text-center">
                    Or add this gift card to your cart and pay with your other items
                  </p>
                  <Button 
                    variant="outline"
                    onClick={addToCart}
                    disabled={!canProceedToStep3()}
                    className="w-full border-green-500 text-green-600 hover:bg-green-50"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Add to Cart (${finalAmount.toFixed(2)})
                  </Button>
                </div>
              )}
            </Card>
          )}

          {/* Step 3: Payment */}
          {step === 3 && (
            <Card className="p-6">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-purple-500" />
                Payment Method
              </h3>

              <div className="space-y-3 mb-6">
                {PAYMENT_METHODS.map((method) => {
                  const Icon = method.icon;
                  const isSelected = selectedPaymentMethod === method.id;
                  return (
                    <button
                      key={method.id}
                      onClick={() => setSelectedPaymentMethod(method.id)}
                      className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                        isSelected ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isSelected ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-600'
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 text-left">
                        <span className="font-medium">{method.name}</span>
                        <p className="text-xs text-gray-500">{method.description}</p>
                      </div>
                      {isSelected && <Check className="w-5 h-5 text-purple-500" />}
                    </button>
                  );
                })}
              </div>

              {/* E-Transfer Details */}
              {selectedPaymentMethod === 'etransfer' && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4">
                  <div>
                    <p className="font-medium text-blue-800 mb-2">E-Transfer Instructions:</p>
                    <ol className="text-sm text-blue-700 space-y-1 list-decimal pl-4">
                      <li>Send ${finalAmount.toFixed(2)} via Interac e-Transfer to: <strong>pay@uplandsfoodmarket.com</strong></li>
                      <li>Use security question: &quot;Store name?&quot; Answer: &quot;UPLANDS&quot;</li>
                      <li>Enter your confirmation number below</li>
                      <li>Upload screenshot of completed transfer (optional but recommended)</li>
                    </ol>
                  </div>
                  
                  <div>
                    <Label>E-Transfer Confirmation Number *</Label>
                    <Input
                      placeholder="Enter confirmation number"
                      value={etransferConfirmation}
                      onChange={(e) => setEtransferConfirmation(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Screenshot (Optional)</Label>
                    <input
                      type="file"
                      ref={etransferScreenshotRef}
                      accept="image/*"
                      onChange={handleEtransferScreenshot}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => etransferScreenshotRef.current?.click()}
                      className="mt-1"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {etransferScreenshot ? 'Change Screenshot' : 'Upload Screenshot'}
                    </Button>
                    {etransferScreenshot && (
                      <p className="text-xs text-green-600 mt-1">✓ Screenshot uploaded</p>
                    )}
                  </div>
                </div>
              )}

              {/* Cash Instructions */}
              {selectedPaymentMethod === 'cash' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="font-medium text-green-800 mb-2">Cash Payment Instructions:</p>
                  <ol className="text-sm text-green-700 space-y-1 list-decimal pl-4">
                    <li>Click &quot;Complete Purchase&quot; to get your reference code</li>
                    <li>Visit UPLANDS FOOD MARKET at: <strong>1234 Merivale Road, Ottawa</strong></li>
                    <li>Show your reference code at the cashier</li>
                    <li>Pay ${finalAmount.toFixed(2)} in cash</li>
                    <li>Gift card will be activated and sent immediately</li>
                  </ol>
                </div>
              )}

              {/* Card Info */}
              {selectedPaymentMethod === 'card' && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Secure Checkout:</strong> You will be redirected to Stripe to complete your payment securely.
                  </p>
                </div>
              )}

              {/* Note about gift card restriction */}
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-6 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Gift cards cannot be purchased using another gift card.
                </p>
              </div>

              {/* Order Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-gray-800 mb-2">Order Summary</h4>
                <div className="flex justify-between text-sm">
                  <span>Gift Card Amount</span>
                  <span className="font-semibold">${finalAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span>To: {recipientName}</span>
                  <span className="text-gray-500">{recipientEmail}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                  Back
                </Button>
                <Button 
                  onClick={handlePurchase}
                  disabled={isProcessing || !canCompletePurchase()}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  {isProcessing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  {selectedPaymentMethod === 'card' ? 'Pay with Card' : 
                   selectedPaymentMethod === 'etransfer' ? 'Submit E-Transfer' : 
                   'Complete Purchase'}
                </Button>
              </div>
            </Card>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <Card className="p-8 text-center">
              {isProcessing ? (
                <>
                  <Loader2 className="w-16 h-16 text-purple-500 animate-spin mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Processing Payment...</h3>
                  <p className="text-gray-600">Please wait while we confirm your payment.</p>
                </>
              ) : purchaseSuccess ? (
                <>
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    {purchaseSuccess.pending ? (
                      <CheckCircle className="w-10 h-10 text-yellow-500" />
                    ) : (
                      <PartyPopper className="w-10 h-10 text-green-500" />
                    )}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">
                    {purchaseSuccess.pending ? 'Order Submitted!' : 'Gift Card Sent!'}
                  </h3>
                  
                  {purchaseSuccess.message && (
                    <p className="text-gray-600 mb-4">{purchaseSuccess.message}</p>
                  )}
                  
                  {!purchaseSuccess.pending && (
                    <p className="text-gray-600 mb-4">
                      Your gift card has been purchased and sent to the recipient.
                    </p>
                  )}

                  <div className="bg-gray-50 rounded-lg p-4 mb-6 inline-block">
                    <p className="text-sm text-gray-500 mb-1">
                      {purchaseSuccess.isCash ? 'Reference Code' : 'Gift Card Code'}
                    </p>
                    <p className="text-2xl font-mono font-bold text-purple-600">
                      {purchaseSuccess.code || 'Pending Activation'}
                    </p>
                  </div>

                  <div className="flex gap-3 justify-center">
                    <Button variant="outline" onClick={resetForm}>
                      Buy Another
                    </Button>
                    <Button onClick={onBack} className="bg-purple-600 hover:bg-purple-700">
                      Back to Shop
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <X className="w-16 h-16 text-red-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Something went wrong</h3>
                  <p className="text-gray-600 mb-4">Please try again or contact support.</p>
                  <Button onClick={() => setStep(3)}>Try Again</Button>
                </>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default GiftCards;
